/**
 * The List: a plain shared to-do. Deliberately boring — it is the ordinary-Tuesday anchor.
 *
 * Four decisions here are load-bearing rather than cosmetic:
 *
 *   THE TICK IS A MOMENT. It pays a point, and it used to pay it with no feedback of any kind on
 *   the row that earned it. It takes the app's stamp band now, exactly as a mission does, and the
 *   band says whether it actually paid — the weekly cap and the "only their items" rule both mean
 *   "ticked" and "banked" are two different facts, and the server is the one that knows which.
 *
 *   THE WEEK'S NUMBERS COME FROM THE SERVER. They are the points ledger, not a count of the done
 *   rows still standing, so tidying up cannot lower your own score. See `weekDone`.
 *
 *   A ROW CAN BE FIXED OR REMOVED. Ticking used to be the only way to get rid of anything, which
 *   made "I typed that wrong" cost a point and put a chore nobody did into the week's tally.
 *
 *   THE FIRST SCREEN IS THE LIST. Two display-size numerals took nearly half of it; the split is
 *   one slim row now, above the box, and the done rows are folded behind a count.
 */
import { Chip } from '@/components/chip';
import { Empty } from '@/components/empty';
import { Panel } from '@/components/panel';
import { Stamped, type Stamp } from '@/components/stamp-band';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { api } from '@/api';
import { otherPartner, partnerTint, type PartnerTint } from '@/lib/partner';
import { cn } from '@/lib/utils';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { useLayout } from '@/useLayout';
import { usePoll } from '@/usePoll';
import { useStore, whoLabel } from '@/store';
import * as React from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';

interface ListItem {
  id: string;
  text: string;
  /** 'shared' is a row with no author — a planted step or an ordinary cover chore, alike. */
  createdBy: 'A' | 'B' | 'shared';
  done: boolean;
  doneBy: 'A' | 'B' | null;
  doneAt: string | null;
  createdAt: string;
}

/**
 * The list plus the week, as the server sends it.
 *
 * `weekDone` is counted from `point_events` server-side rather than from the rows in `items`, and
 * that is the whole point of it: "Clear" hard-deletes, and a tally read off surviving rows made
 * tidying up cost you your own week. What it counts is list points banked — which, since only the
 * other partner's rows pay, is how much of their load each of you picked up.
 */
interface ListPayload {
  items: ListItem[];
  weekDone: { me: number; partner: number };
}

/**
 * THE LIST COLUMN, narrower than the panel's own.
 *
 * The panel caps at 880 so cards do not stretch across a desktop; a to-do row is a single line of
 * text with a caption at the end of it, and at 880 the caption ends up a thousand pixels from the
 * thing it describes with the Clear button beyond that. 720 is where a row still reads as one
 * object. It only bites above its own width, so every phone is unaffected.
 */
const LIST_COLUMN = 'w-full max-w-[720px]';

/** How long a row stays lit after it is added or re-added. Long enough to find it in the list. */
const FLASH_MS = 1400;

/** Case- and space-insensitive: "Bins" and "bins " are one chore, wherever they are compared. */
const norm = (text: string) => text.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Whose row this is, as a colour rather than a word.
 *
 * A row with no author — the server says `'shared'`, which is what an app-planted row and an
 * ordinary cover chore both look like from here — gets a spacer of the same width instead of a
 * dot. `partnerTint` has no entry for it, and inventing one would give the surfacing engine an
 * identity sitting beside the two people, which is the one thing it must never have. The spacer
 * keeps the column of text from jogging, so the row is the same shape as either of theirs.
 *
 * The dot is redundant by design: a partner's row still carries its "X added" caption, which is
 * what a colour-blind reader and a screen reader actually get. Colour is the glance, the word is
 * the fact — and a shared row has neither, because there is no fact to state.
 */
function AuthorDot({ author }: { author: 'A' | 'B' | 'shared' }) {
  // An 8px SQUARE since the restyle: the round dot went with every other pill, and `mark` rather
  // than `fill` is the gel's small-shape reading — B's fill is 1.08:1 on a night card.
  if (author === 'shared') return <View className="size-2" />;
  return <TintSquare tint={partnerTint(author)} />;
}

/**
 * The 8px mark itself, with its edge.
 *
 * The 1px EDGE is what makes the square survive its own surface. B's mark is #1E2532, which is
 * EXACTLY Tuxedo dark's card colour — an edgeless fill there is not a faint square, it is no
 * square at all. Every partner-coloured mark in this app carries `line` for that reason, which is
 * also why the legend in the week row reuses this rather than drawing its own.
 */
function TintSquare({ tint }: { tint: PartnerTint }) {
  return (
    <View
      className="size-2 rounded-[2px]"
      style={{ backgroundColor: tint.mark, borderWidth: 1, borderColor: tint.line }}
    />
  );
}

/**
 * THE WEEK, IN ONE ROW.
 *
 * It was two display-size numerals in two cards, which took about 45% of the first screen on a
 * phone: three list items fitted above the dock and the done rows were below the fold, on a panel
 * whose entire job is the list. The same two numbers on one line say the same thing, and the two
 * author squares double as the legend for the marks running down the rows — which is the only
 * thing that ever said what those colours mean.
 */
function WeekRow({ me, partnerName, weekDone }: { me: 'A' | 'B'; partnerName: string; weekDone: { me: number; partner: number } }) {
  return (
    <View className="flex-row flex-wrap items-center gap-x-2 gap-y-1">
      <Text variant="small" className="text-muted-foreground">
        This week ·
      </Text>
      <View className="flex-row items-center gap-1.5">
        <TintSquare tint={partnerTint(me)} />
        <Text variant="small">
          you <Text variant="small" className="font-semibold">{weekDone.me}</Text>
        </Text>
      </View>
      <Text variant="small" className="text-muted-foreground">
        ·
      </Text>
      <View className="flex-row items-center gap-1.5">
        <TintSquare tint={partnerTint(otherPartner(me))} />
        <Text variant="small">
          {partnerName} <Text variant="small" className="font-semibold">{weekDone.partner}</Text>
        </Text>
      </View>
    </View>
  );
}

/** A chip is a tap target, not a paragraph. The full text is what gets re-added. */
function chipLabel(text: string): string {
  return text.length > 28 ? `${text.slice(0, 27)}…` : text;
}

export function ListScreen() {
  const { me, refreshBadges, fail } = useStore();
  const { wide } = useLayout();
  const [items, setItems] = React.useState<ListItem[]>([]);
  const [weekDone, setWeekDone] = React.useState({ me: 0, partner: 0 });
  const [draft, setDraft] = React.useState('');
  /** The row that was just added, re-added or pointed at. One at a time; it is an answer, not a state. */
  const [flash, setFlash] = React.useState<string | null>(null);
  /** Which row has its overflow open, and which is being edited. Never both, never two. */
  const [menuFor, setMenuFor] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<{ id: string; text: string } | null>(null);
  /** The done rows are folded by default; the count on the fold is what says how many. */
  const [showDone, setShowDone] = React.useState(false);
  /** `Clear all` asks once. A second press inside the beat is the confirmation. */
  const [confirmClear, setConfirmClear] = React.useState(false);
  const [stamp, setStamp] = React.useState<(Stamp & { id: string }) | null>(null);

  /** Whether this mount has already moved the watermark. One stamp per visit, not per poll. */
  const stamped = React.useRef(false);
  const alive = React.useRef(true);
  React.useEffect(() => () => {
    alive.current = false;
  }, []);

  const load = React.useCallback(async () => {
    const payload = await api.get<ListPayload>('/api/list');
    setItems(payload.items);
    setWeekDone(payload.weekDone);
    // Looking at the list is what clears its badge, so the watermark moves when the panel has
    // actually drawn something. Its own catch: a bookkeeping POST that fails is not a reason to
    // say the list would not load when the list is on the screen.
    if (!stamped.current && payload.items.length) {
      stamped.current = true;
      await api
        .post('/api/list/seen')
        .then(() => refreshBadges())
        .catch(() => {});
    }
  }, [refreshBadges]);

  const { loaded, error, reload } = usePoll(load, 30_000);

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await Promise.all([load(), refreshBadges()]);
    } catch (e) {
      fail(e);
    }
  };

  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);

  /** Light a row and let it go out on its own. Cleared on unmount like every other held beat. */
  const lightUp = (id: string) => {
    setFlash(id);
    setTimeout(() => {
      if (alive.current) setFlash((current) => (current === id ? null : current));
    }, FLASH_MS);
  };

  /**
   * Add something, or point at the copy that is already open.
   *
   * The dedupe is the whole reason the re-add chips are safe to tap: a chip is a shortcut for a
   * chore that comes round again, and tapping one twice — or typing a thing that is already on the
   * list — used to make two identical rows nobody wanted. A match FLASHES rather than erroring,
   * because "it is already there, look" is the answer to what the person was trying to do.
   */
  const add = async (text: string) => {
    const existing = open.find((i) => norm(i.text) === norm(text));
    if (existing) {
      lightUp(existing.id);
      return;
    }
    await run(async () => {
      const created = await api.post<ListItem>('/api/list', { text });
      lightUp(created.id);
    });
  };

  /**
   * "Add again" — the chores that come round, minus the ones already open.
   *
   * De-duplicated case- and space-insensitively, and filtered against the open rows, so the same
   * chore can never appear twice on one screen: it used to sit under "Recently done" as a chip AND
   * under "Done" as a struck row with a Clear button beside it, which is one thing wearing two
   * costumes. Not memoised — the list is a household to-do, not a feed.
   */
  const openKeys = new Set(open.map((i) => norm(i.text)));
  const again: ListItem[] = [];
  const seen = new Set<string>();
  for (const item of [...done].sort((a, b) => doneAtMs(b) - doneAtMs(a))) {
    const key = norm(item.text);
    if (seen.has(key) || openKeys.has(key)) continue;
    seen.add(key);
    again.push(item);
    if (again.length === 6) break;
  }

  /**
   * Tick it: the stamp says what it was worth, and the row leaves for the Done fold afterwards.
   *
   * The band is set only once the POST resolves, and it takes its words from the SERVER's answer:
   * a tick pays for the other one's items only, and only up to the weekly cap, so "+1" is a claim
   * this screen is in no position to make on its own. Past the cap it is a bare "Done", which is
   * the honest version of the same moment rather than a fake reward.
   */
  const tick = async (item: ListItem) => {
    try {
      const res = await api.post<{ paid: boolean }>(`/api/list/${item.id}/done`);
      setStamp({ id: item.id, kind: 'done', label: res.paid ? 'Done · +1' : 'Done' });
    } catch (e) {
      setStamp(null);
      fail(e);
    }
  };

  const stampLeft = async () => {
    await Promise.all([load(), refreshBadges()]).catch(() => undefined);
    if (alive.current) setStamp(null);
  };

  const saveEdit = async () => {
    const next = editing;
    if (!next || !next.text.trim()) return;
    setEditing(null);
    setMenuFor(null);
    await run(() => api.put(`/api/list/${next.id}`, { text: next.text.trim() }));
    lightUp(next.id);
  };

  const submitDraft = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    void add(text);
  };

  if (!loaded) return (
    <Panel id="list">
      <PanelSkeleton rows={3} />
    </Panel>
  );
  if (error) return (
    <Panel id="list">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  return (
    <Panel id="list">
      {/* Drawn only once `me` has landed: without knowing which partner is looking, "you" cannot
          be answered, and a guess puts the wrong name and the wrong colour on each half. */}
      {me ? <WeekRow me={me.partner} partnerName={me.partnerName} weekDone={weekDone} /> : null}

      <View className={cn(LIST_COLUMN, 'gap-3')}>
        <View className="flex-row flex-wrap gap-2">
          <Input
            className="min-w-[180px] flex-1"
            value={draft}
            onChangeText={setDraft}
            placeholder="Add something…"
            maxLength={300}
            autoComplete="off"
            onSubmitEditing={submitDraft}
          />
          <Button disabled={!draft.trim()} onPress={submitDraft}>
            <Text>Add</Text>
          </Button>
        </View>

        {again.length ? <AddAgain items={again} onPick={(text) => void add(text)} /> : null}

        {open.length ? (
          open.map((item) => (
            /* The stamp owns the end of a row exactly as it owns the end of a mission card: band,
               hold, and the row collapsing out before the refetch moves it into the Done fold. */
            <Stamped key={item.id} stamp={stamp?.id === item.id ? stamp : null} onLeft={() => void stampLeft()}>
              <Card className={cn(flash === item.id && 'border-primary')}>
                <CardContent className="flex-row items-center gap-3 py-3">
                  <AuthorDot author={item.createdBy} />
                  {/* THE CHECKBOX HAD TO BECOME VISIBLE. `border-input` measured about 1.25:1
                      against a light card and maroon-on-maroon at night, on the only tap target
                      the panel has. `border-foreground/40` clears 3:1 in all four theme blocks,
                      and 22px is the box the boards draw. */}
                  <Pressable
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: false }}
                    accessibilityLabel={`Tick off: ${item.text}`}
                    onPress={() => void tick(item)}
                    className="border-foreground/40 bg-card size-[22px] shrink-0 items-center justify-center rounded-sm border active:opacity-70"
                  />
                  {editing?.id === item.id ? (
                    <View className="min-w-0 flex-1 flex-row flex-wrap items-center gap-2">
                      <Input
                        className="min-w-[140px] flex-1"
                        value={editing.text}
                        onChangeText={(text) => setEditing({ id: item.id, text })}
                        maxLength={300}
                        autoComplete="off"
                        autoFocus
                        onSubmitEditing={() => void saveEdit()}
                      />
                      <Button size="sm" disabled={!editing.text.trim()} onPress={() => void saveEdit()}>
                        <Text>Save</Text>
                      </Button>
                      <Button variant="ghost" size="sm" flush={false} onPress={() => setEditing(null)}>
                        <Text>Cancel</Text>
                      </Button>
                    </View>
                  ) : (
                    <RowBody
                      wide={wide}
                      text={item.text}
                      /* No caption on a shared row, and that is the whole point of this line. It
                         used to say "added by the game" — a label the app wrote on every row the
                         surfacing engine planted, twelve to twenty-four hours after somebody typed
                         a signal. A row that announces where it came from is the receipt this app
                         exists not to leave. */
                      meta={item.createdBy === 'shared' ? null : `${whoLabel(item.createdBy, me)} added`}
                    />
                  )}
                  {/* One glyph, and both answers behind it. Removing pays nothing — which is the
                      fix: ticking used to be the only way to get rid of a row, so a typo cost a
                      point and put a chore nobody did into somebody's week.

                      The glyph STAYS while its two answers are out, and pressing it again puts
                      them away: a menu whose only exits are the two destructive-ish things in it
                      is a menu you cannot back out of. */}
                  {editing?.id === item.id ? null : (
                    <View className="shrink-0 flex-row items-center">
                      {menuFor === item.id ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            flush={false}
                            onPress={() => setEditing({ id: item.id, text: item.text })}>
                            <Text>Edit</Text>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            flush={false}
                            onPress={() => {
                              setMenuFor(null);
                              void run(() => api.delete(`/api/list/${item.id}`));
                            }}>
                            <Text>Remove</Text>
                          </Button>
                        </>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="sm"
                        flush={false}
                        accessibilityLabel={`Edit or remove: ${item.text}`}
                        accessibilityState={{ expanded: menuFor === item.id }}
                        onPress={() => setMenuFor(menuFor === item.id ? null : item.id)}>
                        <Text>⋯</Text>
                      </Button>
                    </View>
                  )}
                </CardContent>
              </Card>
            </Stamped>
          ))
        ) : (
          <Empty mark="✓" title="Nothing on the list" body="Suspicious. Add the thing one of you keeps meaning to do." />
        )}

        {/* THE DONE ROWS ARE FOLDED. They are a record, not a working list, and unfolded they push
            the thing you came here for below the fold on a phone. The count on the fold is what
            says how many, so nothing is hidden — only stacked. */}
        {done.length ? (
          <View className="gap-2 pt-2">
            <View className="flex-row flex-wrap items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                accessibilityState={{ expanded: showDone }}
                onPress={() => setShowDone((s) => !s)}>
                {/* The app's one disclosure glyph, the same pair `WhyThis` uses: U+25B8/U+25BE
                    default to text presentation, so they take the ink of the line rather than
                    rendering as a coloured emoji beside it. */}
                <Text>{`Done (${done.length}) ${showDone ? '▾' : '▸'}`}</Text>
              </Button>
              {showDone ? (
                <Button
                  variant="ghost"
                  size="sm"
                  flush={false}
                  onPress={() => {
                    if (!confirmClear) {
                      setConfirmClear(true);
                      return;
                    }
                    setConfirmClear(false);
                    void run(async () => {
                      for (const item of done) await api.delete(`/api/list/${item.id}`);
                    });
                  }}>
                  {/* Asked once, in place: a second press on the same word is the confirmation,
                      which is the same shape the withdraw and reject confirms use. Nothing is at
                      stake in the ledger — the week's tally is `point_events` and this route does
                      not touch it — so a dialog would be a ceremony about deleting a record of
                      chores. */}
                  <Text>{confirmClear ? 'Clear all — sure?' : 'Clear all'}</Text>
                </Button>
              ) : null}
            </View>
            {showDone
              ? done.map((item) => (
                  <View key={item.id} className="flex-row items-center gap-3 opacity-70">
                    {/* Authorship, not who cleared it: a row's identity does not change when
                        somebody ticks it, and who did it is already the caption at the end. */}
                    <AuthorDot author={item.createdBy} />
                    {/* Gold is the round's one reward value, in both looks — there is no `--success`. */}
                    <Text variant="small" className="text-gold-text">
                      ✓
                    </Text>
                    <RowBody
                      wide={wide}
                      text={item.text}
                      struck
                      meta={item.doneBy ? `${whoLabel(item.doneBy, me)} did it` : null}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      flush={false}
                      className="shrink-0"
                      onPress={() => void run(() => api.delete(`/api/list/${item.id}`))}>
                      <Text>Clear</Text>
                    </Button>
                  </View>
                ))
              : null}
          </View>
        ) : null}
      </View>
    </Panel>
  );
}

/**
 * A row's text and its caption, as ONE column that becomes ONE line when there is room.
 *
 * This is the fix for two rows of the readiness pass at once. At 320 every row wrapped and the
 * caption landed flush against the panel's left edge, under the checkbox rather than under the
 * words it belongs to — because the caption was a sibling of the text in the row's own flex-wrap,
 * so it wrapped to the row's left edge. Putting the two in their own column means the caption
 * hangs under the TEXT at every narrow width, for every row, rather than for whichever ones
 * happened to be long enough to wrap. At bp-wide the same column becomes a line with the caption
 * right-aligned, which is the desktop density the review asked to keep.
 */
function RowBody({
  text,
  meta,
  struck,
  wide,
}: {
  text: string;
  meta: string | null;
  struck?: boolean;
  wide: boolean;
}) {
  return (
    <View className={cn('min-w-0 flex-1 gap-0.5', wide && 'flex-row items-center justify-between gap-3')}>
      <Text
        variant={struck ? 'small' : undefined}
        className={cn('min-w-0 flex-1', struck && 'line-through')}>
        {text}
      </Text>
      {meta ? (
        <Text variant="small" className="text-muted-foreground shrink-0">
          {meta}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * "Add again" — the strip of chores that come round.
 *
 * It used to be titled "Recently done", which described where the chips came FROM rather than what
 * pressing one does, on a strip whose chips write to the list on a single tap with no confirm. The
 * title is the verb now and every chip carries a `+`, so the strip says what it is before it is
 * pressed rather than after.
 *
 * The fade is drawn only when the content actually overflows, and only on the web: it is a CSS
 * gradient with no React Native equivalent, and a flat block of ground colour over the last chip
 * would be a smear rather than a hint. A phone gets the scroll and no fade, which is honest.
 */
function AddAgain({ items, onPick }: { items: { id: string; text: string }[]; onPick: (text: string) => void }) {
  const [viewport, setViewport] = React.useState(0);
  const [content, setContent] = React.useState(0);
  const overflows = content > viewport + 1;

  return (
    <View className="gap-1.5">
      <Text variant="label">Add again</Text>
      <View className="relative">
        {/* `grow-0` so the strip sizes to its own content inside the panel's column instead of
            stretching to fill it, and horizontal because six chores are wider than a phone. */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          className="grow-0"
          contentContainerClassName="gap-2 pr-6"
          onLayout={(e) => setViewport(e.nativeEvent.layout.width)}
          onContentSizeChange={(w) => setContent(w)}>
          {/* The catalogued `Chip`, with the verb in the label rather than a second chip drawing
              invented here: a `+` in front of the chore is what says the tap WRITES, and the
              alternative — a bespoke pressable with its own glyph slot — would be a second thing
              to keep in step with the design file for one screen's sake. */}
          {items.map((item) => (
            <Chip key={item.id} label={`+ ${chipLabel(item.text)}`} onPress={() => onPick(item.text)} />
          ))}
        </ScrollView>
        {overflows && Platform.OS === 'web' ? (
          <View
            pointerEvents="none"
            className="absolute bottom-0 right-0 top-0 w-8"
            style={{ backgroundImage: 'linear-gradient(90deg, transparent, hsl(var(--background)))' } as never}
          />
        ) : null}
      </View>
    </View>
  );
}

/** An instant, or 0 when the server did not say — unknown sorts last rather than being dropped. */
function doneAtMs(item: ListItem): number {
  const t = item.doneAt ? Date.parse(item.doneAt) : NaN;
  return Number.isNaN(t) ? 0 : t;
}
